import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { SkeletonFlightCard } from "@/components/SkeletonCard";
import {
  searchFlights,
  getBookingLinks,
  getIntermediateStops,
  compareTransport,
  type FlightResult,
  type PriceInsights,
} from "@/lib/api";

export const Route = createFileRoute("/flights")({
  component: FlightsPage,
  head: () => ({
    meta: [
      { title: "Vuelos — Sift Trip" },
      { name: "description", content: "Resultados de vuelos con precios reales de múltiples proveedores." },
    ],
  }),
});

function FlightsPage() {
  const rawSearch = Route.useSearch() as Record<string, string>;
  const origin = rawSearch.origin || "";
  const destination = rawSearch.destination || "";
  const date = rawSearch.date || "";
  const adults = Number(rawSearch.adults) || 1;
  const cabinClass = rawSearch.cabin_class || "economy";
  const maxPriceParam = rawSearch.max_price ? Number(rawSearch.max_price) : undefined;
  const maxStopsParam = rawSearch.max_stops !== undefined ? Number(rawSearch.max_stops) : undefined;

  const [flights, setFlights] = useState<FlightResult[]>([]);
  const [priceInsights, setPriceInsights] = useState<PriceInsights | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [totalResults, setTotalResults] = useState(0);

  // Filters
  const [filterStops, setFilterStops] = useState<number | null>(null);
  const [filterMaxPrice, setFilterMaxPrice] = useState(5000);
  const [filterBookingOnly, setFilterBookingOnly] = useState(false);

  // Expandable sections
  const [showStops, setShowStops] = useState(false);
  const [showTransport, setShowTransport] = useState(false);
  const [intermediateStops, setIntermediateStops] = useState<Array<{ city: string; iata: string; reason: string }>>([]);
  const [transportOptions, setTransportOptions] = useState<Array<{ mode: string; price?: number; duration?: string; operator?: string }>>([]);
  const [loadingStops, setLoadingStops] = useState(false);
  const [loadingTransport, setLoadingTransport] = useState(false);

  // Booking modal
  const [bookingModal, setBookingModal] = useState<{
    open: boolean;
    loading: boolean;
    options: Array<{ airline: string; logo?: string; price?: number; url: string }>;
  }>({ open: false, loading: false, options: [] });

  const fetchFlights = useCallback(async () => {
    if (!origin || !destination || !date) return;
    setLoading(true);
    setError("");
    try {
      const res = await searchFlights({
        origin,
        destination,
        date,
        adults,
        cabin_class: cabinClass,
        max_price: maxPriceParam,
        max_stops: maxStopsParam,
      });
      // Handle both response formats: results[] or flights[]
      const flightList = res.results || res.flights || [];
      setFlights(flightList);
      setPriceInsights(res.price_insights || null);
      setTotalResults(res.total_results || flightList.length);
      if (flightList.length) {
        const providers = res.providers_used?.join(", ") || "múltiples proveedores";
        toast.info(`Resultados de ${providers} combinados`);
      }
    } catch {
      setError("😅 Sift Trip tuvo un problema. ¿Intentamos de nuevo?");
    }
    setLoading(false);
  }, [origin, destination, date, adults, cabinClass, maxPriceParam, maxStopsParam]);

  useEffect(() => { fetchFlights(); }, [fetchFlights]);

  const handleShowStops = async () => {
    setShowStops(!showStops);
    if (!showStops && intermediateStops.length === 0) {
      setLoadingStops(true);
      try {
        const res = await getIntermediateStops(origin, destination);
        setIntermediateStops(res.suggestions || []);
      } catch { /* ignore */ }
      setLoadingStops(false);
    }
  };

  const handleShowTransport = async () => {
    setShowTransport(!showTransport);
    if (!showTransport && transportOptions.length === 0) {
      setLoadingTransport(true);
      try {
        const res = await compareTransport({
          from_city: origin,
          from_iata: origin,
          to_city: destination,
          to_iata: destination,
          date,
          adults,
        });
        setTransportOptions(res.options || []);
      } catch { /* ignore */ }
      setLoadingTransport(false);
    }
  };

  const handleBookingLinks = async (flight: FlightResult) => {
    if (!flight.ignav_id) return;
    setBookingModal({ open: true, loading: true, options: [] });
    try {
      const res = await getBookingLinks({
        ignav_id: flight.ignav_id,
        origin,
        destination,
        departure_date: date,
        adults,
      });
      setBookingModal({ open: true, loading: false, options: res.booking_options || [] });
    } catch {
      setBookingModal({ open: true, loading: false, options: [] });
    }
  };

  const filteredFlights = flights.filter((f) => {
    if (filterStops !== null && (f.stops || 0) > filterStops) return false;
    if (f.price && f.price > filterMaxPrice) return false;
    if (filterBookingOnly && !f.booking_url) return false;
    return true;
  });

  const airlines = [...new Set(flights.map((f) => f.airline).filter(Boolean))];

  // Price insights logic - handle both API formats
  const getPriceLevel = () => {
    if (!priceInsights) return null;
    const lowestPrice = priceInsights.lowest ?? priceInsights.lowest_price;
    const typicalLow = priceInsights.typical_range_low ?? priceInsights.typical_price_range?.[0];
    const typicalHigh = priceInsights.typical_range_high ?? priceInsights.typical_price_range?.[1];

    if (lowestPrice && typicalLow && lowestPrice < typicalLow) {
      return { label: "PRECIO BAJO", cls: "badge-low", icon: "✅", lowestPrice, typicalLow, typicalHigh };
    }
    if (lowestPrice && typicalHigh && lowestPrice > typicalHigh) {
      return { label: "PRECIO ALTO", cls: "badge-high", icon: "🔴", lowestPrice, typicalLow, typicalHigh };
    }
    return { label: "PRECIO TÍPICO", cls: "badge-mid", icon: "🟡", lowestPrice, typicalLow, typicalHigh };
  };

  const priceLevel = getPriceLevel();

  const getDuration = (flight: FlightResult) => {
    if (flight.duration) return flight.duration;
    if (flight.duration_minutes) {
      const h = Math.floor(flight.duration_minutes / 60);
      const m = flight.duration_minutes % 60;
      return `${h}h ${m}m`;
    }
    return "";
  };

  return (
    <div className="min-h-screen">
      {/* Top bar */}
      <div className="sticky top-16 z-30 bg-background/90 backdrop-blur border-b border-border py-3 px-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <span className="text-lg">✈️</span>
            <span className="font-bold">{origin} → {destination}</span>
            <span className="text-sm text-muted-foreground">{date} • {adults} viajero{adults > 1 ? "s" : ""}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">{totalResults} vuelos encontrados</span>
            <Link to="/" className="text-sm text-gold hover:underline">Modificar búsqueda</Link>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Price Insights */}
        {priceInsights && priceLevel && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="rhea-card-static p-5 mb-6"
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">📊</span>
              <h3 className="font-bold">Análisis de precio para esta ruta</h3>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <div>
                Precio actual: <span className="font-bold">${priceLevel.lowestPrice}</span>
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-semibold ${priceLevel.cls}`}>
                  {priceLevel.icon} {priceLevel.label}
                </span>
              </div>
              {priceLevel.typicalLow && priceLevel.typicalHigh && (
                <div className="text-muted-foreground">
                  Rango típico: ${priceLevel.typicalLow} – ${priceLevel.typicalHigh}
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-2">Basado en datos históricos de Google Flights vía SerpApi</p>
          </motion.div>
        )}

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Filters sidebar */}
          <div className="lg:w-64 shrink-0">
            <div className="rhea-card-static p-5 space-y-5 lg:sticky lg:top-36">
              <h3 className="font-bold text-sm">Filtros</h3>

              <div>
                <label className="text-xs text-muted-foreground block mb-2">Escalas</label>
                <div className="flex flex-col gap-1">
                  {[{ v: null, l: "Cualquiera" }, { v: 0, l: "Solo directo" }, { v: 1, l: "Máx 1 escala" }].map((opt) => (
                    <button
                      key={String(opt.v)}
                      onClick={() => setFilterStops(opt.v)}
                      className={`text-left text-sm px-3 py-1.5 rounded-lg transition-colors ${filterStops === opt.v ? "bg-accent text-gold" : "text-muted-foreground hover:bg-accent/50"}`}
                    >
                      {opt.l}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs text-muted-foreground block mb-1">Precio máximo: ${filterMaxPrice}</label>
                <input type="range" min={50} max={5000} step={50} value={filterMaxPrice} onChange={(e) => setFilterMaxPrice(Number(e.target.value))} className="w-full accent-gold" />
              </div>

              <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                <input type="checkbox" checked={filterBookingOnly} onChange={(e) => setFilterBookingOnly(e.target.checked)} className="accent-gold" />
                Solo con link de reserva
              </label>

              {airlines.length > 0 && (
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Aerolíneas</label>
                  <div className="text-xs text-muted-foreground space-y-1 max-h-32 overflow-y-auto">
                    {airlines.map((a) => <div key={a}>{a}</div>)}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Results */}
          <div className="flex-1 space-y-4">
            {loading ? (
              <>
                <SkeletonFlightCard />
                <SkeletonFlightCard />
                <SkeletonFlightCard />
                <SkeletonFlightCard />
              </>
            ) : error ? (
              <div className="rhea-card-static p-8 text-center">
                <p className="text-muted-foreground mb-4">{error}</p>
                <button onClick={fetchFlights} className="btn-gold">Reintentar</button>
              </div>
            ) : filteredFlights.length === 0 ? (
              <div className="rhea-card-static p-8 text-center">
                <p className="text-lg mb-2">🔍</p>
                <p className="text-muted-foreground">No encontramos vuelos con esos filtros. Prueba ajustando la fecha o el precio máximo.</p>
                <button onClick={() => { setFilterStops(null); setFilterMaxPrice(5000); setFilterBookingOnly(false); }} className="btn-gold mt-4">Limpiar filtros</button>
              </div>
            ) : (
              filteredFlights.map((flight, i) => (
                <motion.div
                  key={`${flight.flight_number || flight.airline}-${i}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="rhea-card p-5"
                >
                  {flight.is_best && (
                    <div className="mb-2 inline-block px-2 py-0.5 rounded-full text-xs font-bold badge-low">⭐ Mejor precio</div>
                  )}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    {/* Airline */}
                    <div className="flex items-center gap-3 shrink-0">
                      {flight.airline_logo ? (
                        <img src={flight.airline_logo} alt={flight.airline} className="w-10 h-10 rounded-full object-cover bg-accent" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center text-sm font-bold text-gold">
                          {(flight.airline || "?")[0]}
                        </div>
                      )}
                      <div>
                        <div className="font-semibold text-sm">{flight.airline}</div>
                        {flight.flight_number && <div className="text-xs text-muted-foreground">{flight.flight_number}</div>}
                      </div>
                    </div>

                    {/* Times */}
                    <div className="flex-1 flex items-center gap-4">
                      <div className="text-center">
                        <div className="text-lg font-bold">{flight.departure_time || "--:--"}</div>
                        <div className="text-xs text-muted-foreground">{flight.departure_city || origin}</div>
                      </div>
                      <div className="flex-1 flex flex-col items-center">
                        <div className="text-xs text-muted-foreground">{getDuration(flight)}</div>
                        <div className="w-full h-px bg-border relative my-1">
                          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-gold" />
                          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-gold" />
                        </div>
                        <div className={`text-xs font-medium ${(flight.stops || 0) === 0 ? "price-low" : "text-muted-foreground"}`}>
                          {(flight.stops || 0) === 0 ? "✈️ Directo" : `${flight.stops} escala${(flight.stops || 0) > 1 ? "s" : ""}`}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold">{flight.arrival_time || "--:--"}</div>
                        <div className="text-xs text-muted-foreground">{flight.arrival_city || destination}</div>
                      </div>
                    </div>

                    {/* Price & action */}
                    <div className="text-right shrink-0">
                      <div className="text-2xl font-bold text-gold">${flight.price || "—"}</div>
                      <div className="text-xs text-muted-foreground">{flight.cabin_class || cabinClass}</div>
                      {flight.carbon_emissions?.percentage_diff && (
                        <div className="text-xs price-low mt-1">🌱 {flight.carbon_emissions.percentage_diff}% CO₂</div>
                      )}
                    </div>
                  </div>

                  {/* Bottom row */}
                  <div className="mt-3 pt-3 border-t border-border flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      {(flight.source || flight.provider) && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          (flight.source || flight.provider) === "ignav" ? "bg-accent text-blue-400"
                            : (flight.source || flight.provider) === "serpapi" ? "bg-accent text-purple-400"
                            : "bg-accent text-green-400"
                        }`}>
                          {flight.source || flight.provider}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {flight.booking_url ? (
                        <a
                          href={flight.booking_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-gold text-sm py-2 px-4"
                        >
                          Reservar →
                        </a>
                      ) : flight.ignav_id ? (
                        <button
                          onClick={() => handleBookingLinks(flight)}
                          className="text-sm py-2 px-4 rounded-lg border border-gold text-gold hover:bg-gold/10 transition-colors"
                        >
                          Ver opciones de reserva
                        </button>
                      ) : null}
                    </div>
                  </div>
                </motion.div>
              ))
            )}

            {/* Intermediate stops */}
            {!loading && filteredFlights.length > 0 && (
              <div className="mt-8">
                <button onClick={handleShowStops} className="w-full rhea-card-static p-4 text-left flex items-center justify-between hover:bg-accent/50 transition-colors">
                  <span className="font-semibold">📍 ¿Vale la pena hacer una parada?</span>
                  <span className="text-muted-foreground">{showStops ? "▲" : "▼"}</span>
                </button>
                <AnimatePresence>
                  {showStops && (
                    <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="overflow-hidden">
                      <div className="p-4 space-y-3">
                        {loadingStops ? (
                          <div className="skeleton-pulse h-20 rounded-xl" />
                        ) : intermediateStops.length > 0 ? (
                          intermediateStops.map((s) => (
                            <div key={s.iata} className="rhea-card-static p-4 flex items-center justify-between">
                              <div>
                                <span className="font-bold">{s.city}</span>
                                <span className="text-muted-foreground text-sm ml-2">({s.iata})</span>
                                <p className="text-sm text-muted-foreground mt-1">{s.reason}</p>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-muted-foreground text-center py-4">No hay sugerencias de parada para esta ruta</p>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Transport comparison */}
            {!loading && filteredFlights.length > 0 && (
              <div>
                <button onClick={handleShowTransport} className="w-full rhea-card-static p-4 text-left flex items-center justify-between hover:bg-accent/50 transition-colors">
                  <span className="font-semibold">🚆 Comparar con tren y bus</span>
                  <span className="text-muted-foreground">{showTransport ? "▲" : "▼"}</span>
                </button>
                <AnimatePresence>
                  {showTransport && (
                    <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="overflow-hidden">
                      <div className="p-4">
                        {loadingTransport ? (
                          <div className="skeleton-pulse h-20 rounded-xl" />
                        ) : transportOptions.length > 0 ? (
                          <div className="flex gap-3 overflow-x-auto pb-2">
                            {transportOptions.map((t, i) => (
                              <div key={i} className="rhea-card-static p-4 min-w-[180px] shrink-0 text-center">
                                <div className="text-2xl mb-2">
                                  {t.mode === "flight" ? "✈️" : t.mode === "train" ? "🚂" : t.mode === "bus" ? "🚌" : "🚗"}
                                </div>
                                <div className="font-bold text-gold">{t.price ? `$${t.price}` : "—"}</div>
                                <div className="text-sm text-muted-foreground">{t.duration || "—"}</div>
                                {t.operator && <div className="text-xs text-muted-foreground mt-1">{t.operator}</div>}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground text-center py-4">Solo disponible vuelos para esta ruta</p>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      <AnimatePresence>
        {bookingModal.open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
            onClick={() => setBookingModal({ open: false, loading: false, options: [] })}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="rhea-card-static p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-lg">Dónde reservar este vuelo</h3>
                <button onClick={() => setBookingModal({ open: false, loading: false, options: [] })} className="text-muted-foreground hover:text-foreground text-xl">×</button>
              </div>
              {bookingModal.loading ? (
                <div className="space-y-3">
                  <div className="skeleton-pulse h-14 rounded-xl" />
                  <div className="skeleton-pulse h-14 rounded-xl" />
                </div>
              ) : bookingModal.options.length > 0 ? (
                <div className="space-y-3">
                  {bookingModal.options.map((opt, i) => (
                    <a
                      key={i}
                      href={opt.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-3 rounded-xl bg-accent hover:bg-accent/80 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {opt.logo ? <img src={opt.logo} className="w-8 h-8 rounded" alt="" /> : null}
                        <span className="font-medium">{opt.airline}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {opt.price && <span className="font-bold text-gold">${opt.price}</span>}
                        <span className="text-gold">→</span>
                      </div>
                    </a>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No se encontraron opciones de reserva</p>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
